import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { AuthShell } from "@/components/auth/AuthShell";
import { hostedLoginWithAuth0, hostedLogout } from "@/lib/cloud-auth";
import { useAuth } from "@/lib/auth-context";
import {
  type HostedAuth0RedirectOptions,
  auth0Enabled,
  consumeHostedAuth0Callback,
  consumeHostedAuth0Error,
  consumeHostedAuthReturnTo,
  hostedAuth0StartURL,
  readAuth0SwitchFromEmail,
  relayHostedAuth0CallbackToPrimaryOrigin,
  storeHostedAuth0Error,
  writeAuth0LoggedOutMarker,
  writeAuth0SwitchFromEmail,
} from "@/lib/auth0";
import {
  createHostedAuth0CallbackAttempt,
  hostedAuth0FailureMessage,
} from "@/lib/hosted-auth-callback";
import { PRODUCT_NAME } from "@/lib/product";

export const Route = createFileRoute("/login")({
  head: () => ({ meta: [{ title: `Sign in - ${PRODUCT_NAME}` }] }),
  component: LoginPage,
});

export function LoginPage() {
  const { ready, user } = useAuth();
  const [authError, setAuthError] = useState("");
  const callbackAttemptRef = useRef(createHostedAuth0CallbackAttempt());
  const redirectStartedRef = useRef(false);

  const redirectToAuth0 = (options?: HostedAuth0RedirectOptions) => {
    if (typeof window === "undefined" || redirectStartedRef.current) return;
    if (!auth0Enabled()) return;

    const query = new URLSearchParams(window.location.search);
    const loginHint = String(
      query.get("login_hint") || query.get("email") || query.get("identifier") || "",
    )
      .trim()
      .toLowerCase();
    const switchFromEmail = String(
      query.get("switch_from_email") || query.get("disallowed_email") || "",
    )
      .trim()
      .toLowerCase();
    if (switchFromEmail) writeAuth0SwitchFromEmail(switchFromEmail);

    redirectStartedRef.current = true;
    writeAuth0LoggedOutMarker(false);
    window.location.replace(
      hostedAuth0StartURL({
        forceAccountSelection: true,
        expectedEmail: loginHint,
        loginHint,
        disallowedEmail: readAuth0SwitchFromEmail(),
        maxAge: 0,
        ...options,
      }),
    );
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (authError) return;
    if (relayHostedAuth0CallbackToPrimaryOrigin()) return;

    const callbackAttempt = callbackAttemptRef.current;
    const callback = callbackAttempt.hasStarted() ? null : consumeHostedAuth0Callback();
    const result = callbackAttempt.claim(callback, async (claimedCallback) => {
      if (claimedCallback.error) throw new Error(claimedCallback.error);
      if (!claimedCallback.idToken) throw new Error("Auth0 did not return a sign-in token.");

      const nextUser = await hostedLoginWithAuth0({
        idToken: claimedCallback.idToken,
        expectedEmail: claimedCallback.expectedEmail,
        disallowedEmail: claimedCallback.disallowedEmail,
      });
      const blockedEmail = String(claimedCallback.disallowedEmail || "").trim().toLowerCase();
      if (blockedEmail && String(nextUser.email || "").trim().toLowerCase() === blockedEmail) {
        await hostedLogout().catch(() => undefined);
        throw new Error(
          "The previous account was selected again. Retry to sign in with it normally, or choose another account.",
        );
      }
    });

    if (result) {
      void result.then((outcome) => {
        if (outcome.ok) {
          writeAuth0LoggedOutMarker(false);
          writeAuth0SwitchFromEmail("");
          window.location.replace(`${window.location.origin}${consumeHostedAuthReturnTo()}`);
          return;
        }

        const message =
          outcome.error instanceof Error && outcome.error.message.trim()
            ? outcome.error.message
            : "Hosted sign-in could not be completed.";
        storeHostedAuth0Error(message);
        setAuthError(message);
      });
      return;
    }

    if (callbackAttempt.hasStarted()) return;

    const query = new URLSearchParams(window.location.search);
    const authErrorCode = String(query.get("auth_error") || "").trim();
    if (authErrorCode) {
      setAuthError(hostedAuth0FailureMessage(authErrorCode, consumeHostedAuth0Error()));
      writeAuth0SwitchFromEmail("");
      return;
    }

    if (!ready) return;
    if (user?.id) {
      window.location.replace(`${window.location.origin}${consumeHostedAuthReturnTo()}`);
      return;
    }

    redirectToAuth0();
  }, [authError, ready, user?.id]);

  if (!authError) {
    return <div className="genrl-route-canvas min-h-screen w-full" aria-hidden="true" />;
  }

  return (
    <AuthShell
      title="Sign in"
      subtitle="Auth0 returned to GENRL, but the app session was not completed."
    >
      <div className="space-y-4">
        <p
          role="alert"
          className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {authError}
        </p>
        <button
          type="button"
          onClick={() => {
            consumeHostedAuth0Error();
            setAuthError("");
            redirectToAuth0({ forceAccountSelection: true, maxAge: 0 });
          }}
          className="flex h-11 w-full items-center justify-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:opacity-95"
        >
          Try sign-in again
        </button>
      </div>
    </AuthShell>
  );
}
